import { Component, HostListener, OnInit } from "@angular/core";
import { ColDef, GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../services";
import { LoadingScreenService } from "../shared/loading-screen.service";
import { GridViewBaseComponent } from "../utility/grid-view-base.component";
import { SharedDataService } from "../services/sharedDataService";
import { extendDefaultOptions, agDateColumnFilter } from "../utility/gridHelper";
import { LinkCellComponent } from "../shared/grid/link-cell.component";
import { ColParams } from "../shared/grid/ag-grid";

// Interface for Signoff grid - shows demands with pending signoffs
interface SignoffRecord {
  DemandId: number;
  ProjectTitle: string;
  ProjectOid: string;
  ProjectType: string;
}

@Component({
  standalone: false,
  templateUrl: "./signoff.component.html",
  styleUrls: ["../styles.scss", "./capacity-blueprint.scss"],
})
export class CapacityBlueprintSignoffComponent extends GridViewBaseComponent implements OnInit {
  gridOptions!: GridOptions<SignoffRecord>;
  gridApi!: GridApi;
  rowData: SignoffRecord[] = [];

  initPageSize = 15;
  keyFilterStats: string = "filter:capacity-blueprint-signoff";

  constructor(
    protected apiService: ApiService,
    private loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    private notificationService: ToastrService
  ) {
    super(apiService, sharedDataService);
  }

  ngOnInit() {
    this.initializeGrid();
  }

  private initializeGrid(): void {
    const demandIdLinkParams = {
      target: "_self",
      url: (params: ColParams<SignoffRecord, number>) => params.data ? `/quota/capacity/demand/${params.data.DemandId}` : "",
    };

    const columnDefs: ColDef[] = [
      {
        headerName: "Demand ID",
        field: "DemandId",
        filter: "agNumberColumnFilter",
        cellRenderer: LinkCellComponent,
        cellRendererParams: demandIdLinkParams,
      },
      {
        headerName: "Project Title",
        field: "ProjectTitle",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Project OID",
        field: "ProjectOid",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Project Type",
        field: "ProjectType",
        filter: "agTextColumnFilter",
      },
    ];

    this.gridOptions = extendDefaultOptions({
      columnDefs: columnDefs,
      paginationPageSize: this.initPageSize,
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
    });
  }

  onGridReady(params: GridReadyEvent): void {
    super.onGridReady(params);
    this.gridApi = params.api;
    this.loadSignoffData();
  }

  private async loadSignoffData(): Promise<void> {
    this.loadingService.setLoading(true);
    try {
      // Load only Created demands and projects in parallel
      const [createdDemands, allProjects] = await Promise.all([
        this.apiService.getDemandsByStatus("Created").toPromise(),
        this.apiService.getProjects().toPromise(),
      ]);

      const demandsById = new Map<number, any>((createdDemands || []).map((d: any) => [d.Id, d]));
      const projectsById = new Map<number, any>((allProjects || []).map((p: any) => [p.Id, p]));

      const pendingDemandIds = new Set<number>(demandsById.keys());

      this.rowData = Array.from(pendingDemandIds).map((demandId) => {
        const demand = demandsById.get(demandId);
        const project = demand ? projectsById.get(demand.ProjectId) : null;
        return {
          DemandId: demandId,
          ProjectTitle: project?.Title ?? "",
          ProjectOid: project?.ProjectOid ?? "",
          ProjectType: project?.ProjectType ?? "",
        };
      });

      setTimeout(() => {
        this.gridApi?.autoSizeAllColumns();
        if (this.rowData.length === 0) {
          this.gridApi?.showNoRowsOverlay();
        }
      }, 100);
    } catch (error: any) {
      this.notificationService.error(`Failed to load data: ${error?.message || error}`);
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  refreshData(): void {
    this.loadSignoffData();
  }

  @HostListener("window:resize")
  onResize(): void {
    setTimeout(() => {
      this.gridApi?.autoSizeAllColumns();
    }, 100);
  }
}
